import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Book, MagnifyingGlass, Eye, Heart, BookmarkSimple, ShareNetwork,
  Clock, TrendUp, Sparkle, Fire, BookOpen, Article, Pen, Quotes,
  FunnelSimple, X, ArrowRight, CaretDown, User
} from '@phosphor-icons/react';
import GlobalHeader from '../components/GlobalHeader';
import Footer from '../components/Footer';
import { createSafeRichHTML } from '../lib/sanitize';

const API = process.env.REACT_APP_BACKEND_URL;

// Category-specific default cover images
const categoryDefaultCovers = {
  'Poem': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/b01f0103436de493895554b054b25662300512b1e9ff2a4c9bd78bb5c80d0d03.jpeg',
  'Story': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/2dc7d4cb96fbef652e6a5f0244402d4345d16295823a07d013822f17e5b06569.jpeg',
  'Short Story': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/2dc7d4cb96fbef652e6a5f0244402d4345d16295823a07d013822f17e5b06569.jpeg',
  'Novel': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/91be7edf544b13fb8f29fb82ecf244d8400603dd57558e235d2e5e4b41e6ca4f.jpeg',
  'Essay': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/d26f5d5c254c18a0487dbeec31ec1a2c335dfdc1ed050425fc3186c460161f00.jpeg',
  'Ghazal': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/54829e76d70efc219a9133723b4dbe9a67286e5cec56adea02041fb3e2f9c0f2.jpeg',
  'Doha': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/ed3fb42f8b151e354f3fc32e3e24b7978f59241701d2ccc92cc329180792162b.jpeg',
  'Bhajan': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/ed3fb42f8b151e354f3fc32e3e24b7978f59241701d2ccc92cc329180792162b.jpeg',
  'Shayari': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/289629dd888c55b809a3d8b320b1274d665f8b6324944e3cdffd23f7b3fd40b7.jpeg',
  'Article': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/d26f5d5c254c18a0487dbeec31ec1a2c335dfdc1ed050425fc3186c460161f00.jpeg',
  'Blog': 'https://static.prod-images.emergentagent.com/jobs/699f1a31-551c-4a29-a4cb-861bb57180ef/images/d26f5d5c254c18a0487dbeec31ec1a2c335dfdc1ed050425fc3186c460161f00.jpeg',
};

// Helper function to get cover image with priority
const getCoverImage = (item) => {
  // Priority 1: Original uploaded cover
  if (item.featured_image || item.cover_image) {
    return item.featured_image || item.cover_image;
  }
  
  // Priority 2: Category-specific default cover
  if (item.category && categoryDefaultCovers[item.category]) {
    return categoryDefaultCovers[item.category];
  }
  
  // Priority 3: Generic fallback based on work type
  return categoryDefaultCovers['Poem']; // Default fallback
};

// Helper to strip HTML tags
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Category icons mapping
const categoryIcons = {
  'Poem': <BookOpen size={20} weight="duotone" />,
  'Story': <Book size={20} weight="duotone" />,
  'Short Story': <Book size={20} weight="duotone" />,
  'Novel': <Book size={20} weight="duotone" />,
  'Essay': <Article size={20} weight="duotone" />,
  'Ghazal': <Pen size={20} weight="duotone" />,
  'Doha': <Quotes size={20} weight="duotone" />,
  'Bhajan': <Sparkle size={20} weight="duotone" />,
  'Shayari': <Heart size={20} weight="duotone" />,
  'Article': <Article size={20} weight="duotone" />,
  'Blog': <Pen size={20} weight="duotone" />,
};

const categories = [
  { id: 'All', label: 'All', icon: <BookOpen size={20} weight="duotone" /> },
  { id: 'Poem', label: 'Poems', icon: categoryIcons['Poem'] },
  { id: 'Story', label: 'Stories', icon: categoryIcons['Story'] },
  { id: 'Short Story', label: 'Short Stories', icon: categoryIcons['Short Story'] },
  { id: 'Novel', label: 'Novels', icon: categoryIcons['Novel'] },
  { id: 'Essay', label: 'Essays', icon: categoryIcons['Essay'] },
  { id: 'Ghazal', label: 'Ghazals', icon: categoryIcons['Ghazal'] },
  { id: 'Doha', label: 'Dohas', icon: categoryIcons['Doha'] },
  { id: 'Bhajan', label: 'Bhajans', icon: categoryIcons['Bhajan'] },
  { id: 'Shayari', label: 'Shayari', icon: categoryIcons['Shayari'] },
  { id: 'Article', label: 'Articles', icon: categoryIcons['Article'] },
  { id: 'Blog', label: 'Blogs', icon: categoryIcons['Blog'] },
];

const sortOptions = [
  { value: 'trending', label: 'Trending', icon: <TrendUp size={16} /> },
  { value: 'recent', label: 'Newest', icon: <Sparkle size={16} /> },
  { value: 'popular', label: 'Most Read', icon: <Eye size={16} /> },
  { value: 'liked', label: 'Most Liked', icon: <Heart size={16} /> },
  { value: 'random', label: 'Random Discovery', icon: <Sparkle size={16} /> },
];

export const BrowseEnhanced = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'All');
  const [sort, setSort] = useState('trending');
  const [articles, setArticles] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [stats, setStats] = useState({ works: 0, authors: 0, categories: 0, reads: 0 });
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  // Fetch platform stats
  useEffect(() => {
    axios.get(`${API}/api/stats`)
      .then(r => setStats({
        works: r.data.poems || '10,000+',
        authors: r.data.authors || '500+',
        categories: categories.length - 1,
        reads: '1M+'
      }))
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetchResults();
  }, [category, sort]);

  // Search suggestions
  useEffect(() => {
    if (query.length > 2) {
      const timeoutId = setTimeout(() => {
        // Simulate autocomplete (in production, call API)
        const mockSuggestions = articles.slice(0, 5).map(a => a.title);
        setSuggestions(mockSuggestions);
        setShowSuggestions(true);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setShowSuggestions(false);
    }
  }, [query, articles]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (category !== 'All') params.set('category', category);
      params.set('sort', sort);
      
      const res = await axios.get(`${API}/api/search?${params.toString()}`);
      setArticles(res.data.articles);
      setAuthors(res.data.authors);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ search: query, category });
    setShowSuggestions(false);
    fetchResults();
  };

  const getCategoryCount = (catId) => {
    if (catId === 'All') return articles.length;
    return articles.filter(a => a.category === catId).length;
  };

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      {/* Hero Section with Stats */}
      <section className="relative border-b border-border bg-muted/20">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="max-w-3xl">
            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-foreground mb-4 tracking-tight">
              Discover Hindi Literature
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Explore timeless classics, contemporary writings, and hidden gems from legendary authors to emerging voices.
            </p>
            
            {/* Platform Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="font-serif text-3xl font-semibold text-primary">{stats.works}</div>
                <div className="text-sm text-muted-foreground mt-1">Literary Works</div>
              </div>
              <div>
                <div className="font-serif text-3xl font-semibold text-primary">{stats.authors}</div>
                <div className="text-sm text-muted-foreground mt-1">Authors</div>
              </div>
              <div>
                <div className="font-serif text-3xl font-semibold text-primary">{stats.categories}</div>
                <div className="text-sm text-muted-foreground mt-1">Categories</div>
              </div>
              <div>
                <div className="font-serif text-3xl font-semibold text-primary">{stats.reads}</div>
                <div className="text-sm text-muted-foreground mt-1">Total Reads</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Intelligent Search */}
        <form onSubmit={handleSearch} className="mb-8 relative" ref={searchRef}>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title, author, genre, theme, or keywords..."
                className="w-full pl-12 pr-4 py-4 bg-card border border-border rounded-full outline-none focus:border-primary transition-colors text-base"
              />
              
              {/* Search Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setQuery(suggestion);
                        setShowSuggestions(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-muted transition-colors text-sm border-b border-border last:border-b-0"
                    >
                      <MagnifyingGlass size={14} className="inline mr-2 text-muted-foreground" />
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <button
              type="submit"
              className="bg-primary text-primary-foreground rounded-full py-4 px-8 font-semibold hover:opacity-90 transition-opacity"
            >
              Search
            </button>
            
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="border border-border rounded-full py-4 px-6 font-semibold hover:border-primary transition-colors flex items-center gap-2"
            >
              <FunnelSimple size={18} />
              Filters
            </button>
          </div>
        </form>

        {/* Modern Category Navigation */}
        <div className="mb-8 overflow-x-auto pb-2 hide-scrollbar">
          <div className="flex gap-3 min-w-max">
            {categories.map((cat) => {
              const count = getCategoryCount(cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    category === cat.id 
                      ? 'bg-primary text-primary-foreground shadow-md' 
                      : 'bg-card border border-border hover:border-primary'
                  }`}
                >
                  {cat.icon}
                  <span>{cat.label}</span>
                  {count > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      category === cat.id ? 'bg-primary-foreground/20' : 'bg-muted'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sort Options */}
        <div className="flex items-center gap-4 mb-8 pb-4 border-b border-border">
          <span className="text-sm text-muted-foreground font-medium">Sort by:</span>
          <div className="flex gap-3 flex-wrap">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSort(opt.value)}
                className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-full transition-all ${
                  sort === opt.value 
                    ? 'bg-primary text-primary-foreground font-semibold' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {opt.icon}
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Authors Section */}
            {authors.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-serif text-3xl font-semibold text-foreground">
                    Authors
                  </h3>
                  <Link to="/authors" className="text-primary font-semibold text-sm flex items-center gap-1 hover:underline">
                    View all <ArrowRight size={14} />
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {authors.slice(0, 6).map((author) => (
                    <Link
                      key={author.user_id}
                      to={`/profile/${author.user_id}`}
                      className="bg-card border border-border rounded-lg p-5 flex items-center gap-4 hover:border-primary hover:shadow-md transition-all"
                    >
                      <img
                        src={author.picture || `https://ui-avatars.com/api/?name=${author.name}`}
                        alt={author.name}
                        className="w-14 h-14 rounded-full border-2 border-border"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">{author.name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <User size={12} /> {author.followers_count || 0} followers
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Main Content Section */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-3xl font-semibold text-foreground">
                  {category === 'All' ? 'All Content' : category}
                  <span className="text-xl text-muted-foreground ml-2">({articles.length})</span>
                </h3>
              </div>
              
              {articles.length === 0 ? (
                <EmptyState category={category} onReset={() => { setCategory('All'); setQuery(''); fetchResults(); }} />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {articles.map((item) => (
                    <WorkCard key={`${item.source_type}-${item.article_id}`} item={item} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {/* Footer CTA */}
      <section className="bg-muted/30 border-t border-border py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="font-serif text-3xl font-semibold text-foreground mb-4">
            Can't find what you're looking for?
          </h3>
          <p className="text-muted-foreground mb-8">
            Explore our full collection of authors, curated collections, and literary movements.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/authors" className="px-6 py-3 bg-card border border-border rounded-full font-semibold hover:border-primary transition-colors">
              Browse Authors
            </Link>
            <Link to="/collections" className="px-6 py-3 bg-card border border-border rounded-full font-semibold hover:border-primary transition-colors">
              Explore Collections
            </Link>
            <Link to="/editor" className="px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:opacity-90 transition-opacity">
              Become a Writer
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* Enhanced Work Card Component */
function WorkCard({ item }) {
  const isWork = item.source_type === 'work';
  const href = isWork ? `/work/${item.work_id || item.article_id}` : `/article/${item.article_id}`;
  const readingTime = item.reading_time || Math.ceil((item.content?.length || 0) / 1000);
  const coverImage = getCoverImage(item);
  const description = stripHtml(item.meta_description || item.excerpt || item.description || '');

  return (
    <Link
      to={href}
      className="group bg-card border border-border rounded-lg overflow-hidden hover:border-primary hover:shadow-xl transition-all duration-300"
    >
      {/* High-Quality Cover Image */}
      <div className="aspect-[3/4] bg-muted overflow-hidden relative">
        <img 
          src={coverImage}
          alt={item.title} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
          loading="lazy"
        />
        
        {/* Category Badge Overlay */}
        <div className="absolute top-3 left-3">
          <span className="text-[10px] font-bold uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1.5 rounded-full shadow-lg">
            {item.category}
          </span>
        </div>
        
        {/* Classical Badge */}
        {isWork && (
          <div className="absolute top-3 right-3">
            <span className="text-[9px] font-bold uppercase tracking-widest bg-amber-500 text-white px-2.5 py-1 rounded-full shadow-lg">
              Classical
            </span>
          </div>
        )}
        
        {/* Read Now Button on Hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
          <button className="bg-primary text-primary-foreground px-6 py-2.5 rounded-full font-semibold text-sm shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
            Read Now
          </button>
        </div>
      </div>
      
      <div className="p-5">
        {/* Title */}
        <h4 className="font-serif text-xl font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors leading-tight">
          {item.title}
        </h4>
        
        {/* Hindi Title */}
        {item.title_hindi && item.title_hindi !== item.title && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-1 font-medium" style={{ fontFamily: 'Mukta, sans-serif' }}>
            {item.title_hindi}
          </p>
        )}
        
        {/* Author */}
        <p className="text-sm text-muted-foreground mb-3 flex items-center gap-1.5">
          <User size={14} className="shrink-0" />
          <span className="truncate">{item.author_name || 'Anonymous'}</span>
        </p>
        
        {/* Description */}
        {description && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2 leading-relaxed">
            {description}
          </p>
        )}
        
        {/* Meta Info */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-border">
          <div className="flex items-center gap-3">
            {readingTime > 0 && (
              <span className="flex items-center gap-1" title="Reading time">
                <Clock size={13} weight="duotone" />
                {readingTime} min
              </span>
            )}
            <span className="flex items-center gap-1" title="Views">
              <Eye size={13} weight="duotone" />
              {item.views || 0}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1" title="Likes">
              <Heart size={13} weight="duotone" />
              {item.likes || 0}
            </span>
            <button 
              onClick={(e) => { e.preventDefault(); /* TODO: Bookmark */ }}
              className="flex items-center gap-1 hover:text-primary transition-colors"
              title="Bookmark"
            >
              <BookmarkSimple size={13} weight="duotone" />
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}

/* Empty State Component */
function EmptyState({ category, onReset }) {
  return (
    <div className="bg-card border border-border rounded-lg p-16 text-center">
      <div className="max-w-md mx-auto">
        <Book size={64} weight="duotone" className="text-muted-foreground mx-auto mb-6 opacity-50" />
        <h3 className="font-serif text-2xl font-semibold text-foreground mb-3">
          No {category === 'All' ? 'content' : category.toLowerCase()} found
        </h3>
        <p className="text-muted-foreground mb-8">
          Try adjusting your filters or search query to find what you're looking for.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onReset}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:opacity-90 transition-opacity"
          >
            Clear Filters
          </button>
          <Link
            to="/browse"
            className="px-6 py-3 bg-card border border-border rounded-full font-semibold hover:border-primary transition-colors"
          >
            Browse All Works
          </Link>
        </div>
      </div>
    </div>
  );
}

export default BrowseEnhanced;
